import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary-500 text-primary-900 hover:bg-primary-600 rounded-sm',
        destructive: 'bg-danger-500 text-white hover:bg-danger-600 rounded-sm',
        outline:
          'border border-border-light bg-background-secondary text-text-primary hover:bg-canvas-soft-2 rounded-sm',
        secondary:
          'bg-background-secondary text-text-primary border border-border-light hover:bg-canvas-soft-2 rounded-sm',
        ghost: 'text-text-secondary hover:bg-canvas-soft-2 hover:text-text-primary rounded-sm',
        link: 'text-link underline-offset-4 hover:underline rounded-sm',
        pill: 'bg-primary-500 text-primary-900 hover:bg-primary-600 rounded-pill',
        'pill-outline':
          'bg-background-secondary text-text-primary border border-border-light hover:bg-canvas-soft-2 rounded-pill',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-8 px-3 text-xs',
        lg: 'h-12 px-6 text-base',
        pill: 'h-12 px-6 text-base',
        'pill-sm': 'h-7 px-3 text-sm',
        icon: 'h-10 w-10 rounded-sm',
        'icon-sm': 'h-8 w-8 rounded-sm',
        'icon-lg': 'h-12 w-12 rounded-sm',
        'icon-circle': 'h-9 w-9 rounded-full border border-border-light bg-background-secondary',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

const Button = React.forwardRef(
  ({ className, variant, size, asChild = false, style, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    const resolvedVariant =
      variant === 'gradient' ? 'default' : variant;
    return (
      <Comp
        className={cn(buttonVariants({ variant: resolvedVariant, size, className }))}
        ref={ref}
        style={style}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
